import React, { CSSProperties } from 'react';

import { Application } from './application';
import { getAppName } from './quick-access-link';
import { gridContainerStyle } from "./styles";
import { Cards } from './cards';
import { CardHeader } from "./card-header";
import { AppCard } from "../shared/app-card";
import { ApplicationLink } from './quick-navigation-panel';

import skymodLogoImage from "../../content/gmw-standard.png";
import scenariosLogoImage from "../../content/scenarios-resized.png";
import scenarioAssistantLogoImage from "../../content/scenario-assistant.png";
import { Forecast } from '../../model/forecast';

const GMWBulletPoints = [
    "Compare results across scenarios",
    "Undertake multiple scenarios simultaneously",
    "Collaborate online with colleagues",
]

export function renderRunModelSection(
    navBuilder: (applicationLink: ApplicationLink) => (navigate: boolean, forecastIdUrlPart?: string) => (e: React.MouseEvent) => void,
    skymodUi: string,
    scenariosUrl: string,
    scenarioBotUrl: string,
    showScenarioBot: boolean,
    detailedMode: boolean,
    selectedForecast: Forecast | undefined,
    forecastIdUrlPart: string,
    imageStyle: CSSProperties
): JSX.Element {

    const scenariosForecastUrlPart: string = selectedForecast
        ? `&forecastId=${selectedForecast.id}@${selectedForecast.latestVersion}`
        : "";

    const skymodNavHandler: (navigate: boolean, forecastIdUrlPart?: string) => (e: React.MouseEvent) => void =
        navBuilder({ name: Application.Skymod, url: skymodUi });
    const scenarioNavHandler: (navigate: boolean, forecastIdUrlPart?: string) => (e: React.MouseEvent) => void =
        navBuilder({ name: Application.Scenarios, url: scenariosUrl });
    const scenarioAssistantNavHandler: (navigate: boolean, forecastIdUrlPart?: string) => (e: React.MouseEvent) => void =
        navBuilder({ name: Application.ScenarioAssistant, url: scenarioBotUrl });

    const skymodLogo: JSX.Element = <img style={imageStyle} alt="Global Economic Model logo"
        src={skymodLogoImage} onClick={skymodNavHandler(true, forecastIdUrlPart)} />;

    const scenariosLogo: JSX.Element = <img style={imageStyle} alt="Predefined scenarios"
        src={scenariosLogoImage} onClick={scenarioNavHandler(true, scenariosForecastUrlPart)} />;

    const scenarioAssistantLogo: JSX.Element = <img style={imageStyle} alt="Model Assistant"
        src={scenarioAssistantLogoImage} onClick={scenarioAssistantNavHandler(true)} />;

    const showScenariosCard: boolean = !selectedForecast || selectedForecast?.economicDomain.code === "MACRO";

    const modelApplicationTitle: string = selectedForecast
        ? getAppName(Application.Skymod, selectedForecast)
        : Application.Skymod;

    return <div style={gridContainerStyle}>
        <CardHeader headerText={Cards.RunModel} showUserGuide={true} userGuidePage={"#global-model-interface"} />
        <AppCard
            url={`${skymodUi}${forecastIdUrlPart}`}
            title={modelApplicationTitle}
            appDescription="Solve an economic model using your own assumptions and quantify the impact on the global economy."
            appDetails={
                <ul>
                    {GMWBulletPoints.map(i => <li key={i}>{i}</li>)}
                </ul>
            }
            showDetails={detailedMode}
            image={skymodLogo}
            linkText={"Launch model"}
            onLinkClick={skymodNavHandler(false)}
            onTitleClick={skymodNavHandler(true, forecastIdUrlPart)}
        />
        {showScenarioBot && <AppCard
            url={scenarioBotUrl}
            title={<span className="scenario-assistant-title">
                {Application.ScenarioAssistant}
                <svg aria-hidden="true" fill="currentColor" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" viewBox="0 0 24 24">
                    <path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z" />
                    <path d="M20 2v4" />
                    <path d="M22 4h-4" />
                    <circle cx="4" cy="20" r="2" />
                </svg>
            </span>}
            appDescription="Generate and explore scenarios in the Global Economic Model using simple language prompts."
            showDetails={detailedMode}
            image={scenarioAssistantLogo}
            linkText={"Start chat"}
            linkButtonType="primary"
            onLinkClick={scenarioAssistantNavHandler(false)}
            onTitleClick={scenarioAssistantNavHandler(true)}
        />}
        {
            showScenariosCard && <AppCard
                url={`${scenariosUrl}${scenariosForecastUrlPart}`}
                title={Application.Scenarios}
                appDescription="Choose an alternative scenario and quantify its impact on the global economy."
                showDetails={detailedMode}
                linkText={"Create scenario"}
                onLinkClick={scenarioNavHandler(false)}
                onTitleClick={scenarioNavHandler(true, scenariosForecastUrlPart)}
                image={scenariosLogo}
            />
        }
    </div>;
}